import clsx from 'clsx';
import { isArray, isBoolean, isNumber, isString } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import { useCallback } from 'react';

import SummaryItem from '@domains/job-detail/components/job-overview/SummaryItem';

import { HighlightsList } from '@hooks/get-job-position.hook';

import femaleLayingDownZoomedIn from '@public/assets/female-laying-down-zoomed-in.png';

import { useRouterHelper } from '@shared/hooks/use-router-helper';

import defaultPerks from '../job-overview/DefaultJobPerks';
import { translatedValue } from '../job-overview/SummaryCard';
import Section, { SectionTitle } from '../section/Section.component';

export interface HighlightsProps {
  perks: HighlightsList;
}

export const Highlights = ({ perks }: HighlightsProps): React.JSX.Element => {
  const { t } = useTranslation('jobDetails', { keyPrefix: 'highlights' });
  const { isTransparentPath } = useRouterHelper();
  const interviewWithTranslation = useCallback(
    (key: string) => {
      if (key === 'interviewWith') return t(`perks.${key}.title`);
      return t(`perks.${key}`);
    },
    [t],
  );

  return (
    <Section>
      <SectionTitle t={t} titleKey='title' />
      <div className='flex flex-col items-center lg:flex-row lg:space-x-8'>
        {!isTransparentPath && (
          <div className='relative hidden h-65 w-109.75 grow lg:block'>
            <Image alt='' src={femaleLayingDownZoomedIn} className='object-contain object-bottom' fill sizes='439px' />
          </div>
        )}
        <div className={clsx('grid w-full grid-cols-1 gap-4 lg:grid-cols-2')}>
          {(Object.entries(perks) as [keyof HighlightsList, string[] | string | number][]).map(([key, value]) =>
            checkValueIsValid(value) ? (
              <SummaryItem
                emoji={defaultPerks[key]}
                key={key}
                label={interviewWithTranslation(key)}
                value={translatedValue(key, Array.isArray(value) ? value : value?.toString() ?? '', t)}
              />
            ) : null,
          )}
        </div>
      </div>
      {!isTransparentPath && (
        <div className='relative h-72 w-full grow lg:hidden'>
          <Image alt='' src={femaleLayingDownZoomedIn} className='object-contain object-bottom-left' fill sizes='100vw' />
        </div>
      )}
    </Section>
  );
};

const checkValueIsValid = (value: string[] | string | number) => {
  if (isArray(value) && value.length > 0) return true;
  if (isNumber(value) && value > 0) return true;
  if (isString(value) && value !== '') return true;
  if (isBoolean(value)) return true;
  return false;
};

export default Highlights;
